import { Box, Typography, Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDarkMode } from "../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

function NoPermission() {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();

  const context = location.state?.context || "admin";

  const colorSchemes = {
    donor: {
      primary: "#669d2e",
      hover: "#558024",
    },
    receiver: {
      primary: "#2196f3",
      hover: "#1976d2",
    },
    admin: {
      primary: "#8b5cf6",
      hover: "#7c3aed",
    },
  };

  const colors = colorSchemes[context] || colorSchemes.admin;

  const handleGoBack = () => {
    // check if there's history to go back to
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: darkMode ? "#1a1a1a" : "#f5f5f5",
        textAlign: "center",
        padding: 3,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          marginBottom: 2,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "80px", md: "150px" },
            fontWeight: 700,
            color: colors.primary,
            fontFamily: "Poppins",
            margin: 0,
          }}
        >
          403
        </Typography>
        <FontAwesomeIcon
          icon={faLock}
          style={{
            fontSize: "70px",
            color: colors.primary,
            opacity: 0.9,
            animation: "gentleShake 2s ease-in-out infinite",
          }}
        />
      </Box>

      <Typography
        variant="h4"
        sx={{
          color: darkMode ? "#ffffff" : "#333333",
          marginBottom: 2,
          fontFamily: "Poppins",
          fontWeight: 600,
        }}
      >
        {t("Access Denied")}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: darkMode ? "#b0b0b0" : "#666666",
          marginBottom: 4,
          maxWidth: "500px",
          fontFamily: "Poppins",
        }}
      >
        {t(
          "You don't have permission to access this page. Please contact your administrator if you believe this is an error.",
        )}
      </Typography>

      {/* buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={handleGoBack}
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            padding: "12px 40px",
            fontSize: "16px",
            fontFamily: "Poppins",
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: colors.hover,
            },
          }}
        >
          {t("Go Back")}
        </Button>
        <Button
          variant="outlined"
          onClick={handleGoHome}
          sx={{
            borderColor: colors.primary,
            color: colors.primary,
            padding: "12px 40px",
            fontSize: "16px",
            fontFamily: "Poppins",
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              borderColor: colors.hover,
              backgroundColor: `${colors.primary}14`,
            },
          }}
        >
          {t("Go to Dashboard")}
        </Button>
      </Box>

      {/* Gentle shake animation */}
      <style>
        {`
          @keyframes gentleShake {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-5deg); }
            75% { transform: rotate(5deg); }
          }
        `}
      </style>
    </Box>
  );
}

export default NoPermission;
